// src/components/programs/ProgramForm/Step2Sections/QuotationSummary.jsx

import React from "react";
import { Box, Grid, Paper, Typography, Divider, Chip, Stack, alpha, useTheme } from "@mui/material";
import {
    // AttachMoney as MoneyIcon,
    TrendingUp as ProfitIcon,
    Assessment as AssessmentIcon,
    People as PeopleIcon,
    CalendarToday as CalendarIcon,
    LocalOffer as DiscountIcon,
} from "@mui/icons-material";
import { formatCurrency, formatNumber } from "../helpers/quotationCalculations";

const QuotationSummary = ({ totals, formData, isArabic, globalDiscount }) => {
    const theme = useTheme();
    const discountTypeOptions = [
        { id: "zero", name: isArabic ? "بدون خصم" : "No Discount" },
        { id: "amount", name: isArabic ? "مبلغ ثابت" : "Fixed Amount" },
        { id: "percent", name: isArabic ? "نسبة مئوية" : "Percentage" },
    ];
    // Compact Stat Card
    const StatCard = ({ icon, label, value, color = "primary", subtitle, numericValue }) => {
        const isNegativeOrZero = numericValue !== undefined ? numericValue <= 0 : false;
        const finalColor = isNegativeOrZero ? "error" : color;
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1.5px solid ${theme.palette[finalColor].main}`,
                    bgcolor: alpha(theme.palette[finalColor].main, 0.05),
                    transition: 'all 0.2s',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        borderColor: theme.palette[finalColor].dark,
                    }
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            bgcolor: theme.palette[finalColor].main,
                            color: 'white',
                        }}
                    >
                        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                        {label}
                    </Typography>
                </Stack>
                <Typography variant="h6" fontWeight={900} color={`${finalColor}.main`} sx={{ mb: subtitle ? 0.5 : 0, fontSize: '1.6rem' }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                        {subtitle}
                    </Typography>
                )}
            </Paper>
        );
    };
    // Compact Detail Row
    const DetailRow = ({ label, value, color = "text.primary", bold = false, isHighlight = false, numericValue }) => {
        const isNegative = numericValue !== undefined ? numericValue <= 0 : (typeof value === 'string' && value.startsWith('-'));
        const finalColor = isNegative ? "error.main" : color;
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    px: isHighlight ? 1.5 : 0,
                    borderRadius: isHighlight ? 1.5 : 0,
                    bgcolor: isHighlight
                        ? alpha(
                            finalColor === "success.main" ? theme.palette.success.main :
                                finalColor === "error.main" ? theme.palette.error.main :
                                    finalColor === "primary.main" ? theme.palette.primary.main :
                                        theme.palette.grey[500],
                            0.08
                        )
                        : 'transparent',
                }}
            >
                <Typography variant="body2" color="text.secondary" fontWeight={bold ? 700 : 600} sx={{ fontSize: '0.9rem' }}>
                    {label}
                </Typography>
                <Typography variant="body2" color={finalColor} fontWeight={bold ? 800 : 700} sx={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>
                    {value}
                </Typography>
            </Box>
        );
    };

    // Check if discount is active
    const hasActiveDiscount = formData.global_discount?.type !== "zero" &&
        formData.global_discount?.target !== "none" &&
        (formData.global_discount?.value || 0) > 0;

    return (
        <Box>
            {/* Compact Top Stats */}
            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                {/* Total Passengers */}
                <Grid item xs={6}>
                    <StatCard
                        icon={<PeopleIcon />}
                        label={isArabic ? "إجمالي الركاب" : "Total Passengers"}
                        value={totals.total_pax}
                        color="info"
                    />
                </Grid>

                {/* Total Days */}
                <Grid item xs={6}>
                    <StatCard
                        icon={<CalendarIcon />}
                        label={isArabic ? "إجمالي الأيام" : "Total Days"}
                        value={formData.durations_days}
                        color="info"
                    />
                </Grid>

                {/* Final Selling Price */}
                <Grid item xs={12}>
                    <StatCard
                        icon={<Typography display="block" fontWeight={800}>฿</Typography>}
                        label={isArabic ? "السعر النهائي" : "Final Selling Price"}
                        value={formatCurrency(totals.final_selling_price)}
                        color="success"
                        numericValue={totals.final_selling_price}
                        subtitle={
                            isArabic
                                ? `للفرد: ${formatCurrency(totals.price_per_person)}`
                                : `Per Person: ${formatCurrency(totals.price_per_person)}`
                        }
                    />
                </Grid>

                {/* Final Cost Price */}
                <Grid item xs={6}>
                    <StatCard
                        icon={<AssessmentIcon />}
                        label={isArabic ? "سعر التكلفة النهائي" : "Final Cost Price"}
                        value={formatCurrency(totals.final_cost_price)}
                        color="error"
                        numericValue={totals.final_cost_price}
                        subtitle={
                            isArabic
                                ? `للفرد: ${formatCurrency(totals.cost_per_person)}`
                                : `Per Person: ${formatCurrency(totals.cost_per_person)}`
                        }
                    />
                </Grid>

                {/* Profit */}
                <Grid item xs={6}>
                    <StatCard
                        icon={<ProfitIcon />}
                        label={isArabic ? "الربح الصافي" : "Net Profit"}
                        value={formatCurrency(totals.final_profit)}
                        color="primary"
                        numericValue={totals.final_profit}
                        subtitle={
                            isArabic
                                ? `هامش الربح: ${formatNumber(totals.profit_margin)}%`
                                : `Margin: ${formatNumber(totals.profit_margin)}%`
                        }
                    />
                </Grid>
            </Grid>

            {/* Compact Detailed Breakdown */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                }}
            >
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2, fontSize: '0.95rem' }}>
                    {isArabic ? "📋 تفاصيل الحسابات" : "📋 Detailed Breakdown"}
                </Typography>

                {/* Selling Price Section */}
                <Box sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                        {/* <MoneyIcon sx={{ fontSize: 16, color: 'success.main' }} /> */}
                        <Typography
                            color="success.main"
                            display="block">
                            ฿
                        </Typography>
                        <Typography variant="caption" fontWeight={700} color="success.main" sx={{ fontSize: '0.8rem' }}>
                            {isArabic ? "سعر البيع" : "Selling Price"}
                        </Typography>
                    </Stack>
                    <Divider sx={{ mb: 1 }} />

                    <DetailRow
                        label={isArabic ? "الإجمالي قبل الخصم" : "Subtotal Before Discount"}
                        value={formatCurrency(totals.subtotal_selling_price)}
                        numericValue={totals.subtotal_selling_price}
                        bold
                    />
                    
                    {totals.breakdown && (
                        <Box sx={{ pl: 2, mb: 1, borderLeft: `2px solid ${alpha(theme.palette.divider, 0.1)}`, ml: 1, py: 0.5 }}>
                            {totals.breakdown.hotel.sell > 0 && (
                                <DetailRow label={isArabic ? "الفنادق" : "Hotels"} value={formatCurrency(totals.breakdown.hotel.sell)} numericValue={totals.breakdown.hotel.sell} />
                            )}
                            {totals.breakdown.tour.sell > 0 && (
                                <DetailRow label={isArabic ? "الجولات السياحية" : "Tours"} value={formatCurrency(totals.breakdown.tour.sell)} numericValue={totals.breakdown.tour.sell} />
                            )}
                            {totals.breakdown.transfer.sell > 0 && (
                                <DetailRow label={isArabic ? "الانتقالات" : "Transfers"} value={formatCurrency(totals.breakdown.transfer.sell)} numericValue={totals.breakdown.transfer.sell} />
                            )}
                            {totals.breakdown.flight.sell > 0 && (
                                <DetailRow label={isArabic ? "الطيران" : "Flights"} value={formatCurrency(totals.breakdown.flight.sell)} numericValue={totals.breakdown.flight.sell} />
                            )}
                            {totals.breakdown.other.sell > 0 && (
                                <DetailRow label={isArabic ? "أخرى" : "Others"} value={formatCurrency(totals.breakdown.other.sell)} numericValue={totals.breakdown.other.sell} />
                            )}
                        </Box>
                    )}

                    {hasActiveDiscount && (formData.global_discount?.target === "sell" || formData.global_discount?.target === "both") && (
                        <DetailRow
                            label={
                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                    <DiscountIcon sx={{ fontSize: 14, color: 'warning.main' }} />

                                    <Typography variant="caption" fontWeight={600}>
                                        {isArabic ? "الخصم الشامل" : "Global Discount"}
                                    </Typography>

                                    <Typography variant="caption" color="text.secondary">
                                        – {
                                            discountTypeOptions?.find(
                                                type => type?.id === globalDiscount?.type
                                            )?.name
                                        }
                                    </Typography>

                                    <Chip
                                        label={
                                            globalDiscount?.type === "percent"
                                                ? `${globalDiscount?.value}%`
                                                : formatCurrency(globalDiscount?.value)
                                        }
                                        size="small"
                                        color="warning"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            borderRadius: 1,
                                        }}
                                    />
                                </Stack>
                            }
                            value={`- ${formatCurrency(totals.discount_on_selling)}`}
                            color="warning.main"
                            numericValue={-Math.abs(totals.discount_on_selling)} // Always negative
                        />

                    )}

                    <Divider sx={{ my: 1 }} />

                    <DetailRow
                        label={isArabic ? "الإجمالي بعد الخصم" : "Total After Discount"}
                        value={formatCurrency(totals.final_selling_price)}
                        color="success.main"
                        bold
                        isHighlight
                        numericValue={totals.final_selling_price}
                    />
                </Box>

                {/* Cost Price Section */}
                <Box sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                        <AssessmentIcon sx={{ fontSize: 16, color: 'error.main' }} />
                        <Typography variant="caption" fontWeight={700} color="error.main" sx={{ fontSize: '0.8rem' }}>
                            {isArabic ? "سعر التكلفة" : "Cost Price"}
                        </Typography>
                    </Stack>
                    <Divider sx={{ mb: 1 }} />

                    <DetailRow
                        label={isArabic ? "الإجمالي قبل الخصم" : "Subtotal Before Discount"}
                        value={formatCurrency(totals.subtotal_cost_price)}
                        numericValue={totals.subtotal_cost_price}
                        bold
                    />

                    {totals.breakdown && (
                        <Box sx={{ pl: 2, mb: 1, borderLeft: `2px solid ${alpha(theme.palette.divider, 0.1)}`, ml: 1, py: 0.5 }}>
                            {totals.breakdown.hotel.cost > 0 && (
                                <DetailRow label={isArabic ? "الفنادق" : "Hotels"} value={formatCurrency(totals.breakdown.hotel.cost)} numericValue={totals.breakdown.hotel.cost} />
                            )}
                            {totals.breakdown.tour.cost > 0 && (
                                <DetailRow label={isArabic ? "الجولات السياحية" : "Tours"} value={formatCurrency(totals.breakdown.tour.cost)} numericValue={totals.breakdown.tour.cost} />
                            )}
                            {totals.breakdown.transfer.cost > 0 && (
                                <DetailRow label={isArabic ? "الانتقالات" : "Transfers"} value={formatCurrency(totals.breakdown.transfer.cost)} numericValue={totals.breakdown.transfer.cost} />
                            )}
                            {totals.breakdown.flight.cost > 0 && (
                                <DetailRow label={isArabic ? "الطيران" : "Flights"} value={formatCurrency(totals.breakdown.flight.cost)} numericValue={totals.breakdown.flight.cost} />
                            )}
                            {totals.breakdown.other.cost > 0 && (
                                <DetailRow label={isArabic ? "أخرى" : "Others"} value={formatCurrency(totals.breakdown.other.cost)} numericValue={totals.breakdown.other.cost} />
                            )}
                        </Box>
                    )}

                    {hasActiveDiscount && (formData.global_discount?.target === "cost" || formData.global_discount?.target === "both") && (
                        <DetailRow
                            label={
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <DiscountIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                    <span>{isArabic ? "الخصم الشامل" : "Global Discount"}</span>

                                    <Typography variant="caption" color="text.secondary">
                                        – {
                                            discountTypeOptions?.find(
                                                type => type?.id === globalDiscount?.type
                                            )?.name
                                        }
                                    </Typography>

                                    <Chip
                                        label={
                                            globalDiscount?.type === "percent"
                                                ? `${globalDiscount?.value}%`
                                                : formatCurrency(globalDiscount?.value)
                                        }
                                        size="small"
                                        color="warning"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            borderRadius: 1,
                                        }}
                                    />
                                </Stack>
                            }
                            value={`- ${formatCurrency(totals.discount_on_cost)}`}
                            color="warning.main"
                            numericValue={-Math.abs(totals.discount_on_cost)}
                        />
                    )}

                    <Divider sx={{ my: 1 }} />

                    <DetailRow
                        label={isArabic ? "الإجمالي بعد الخصم" : "Total After Discount"}
                        value={formatCurrency(totals.final_cost_price)}
                        color="error.main"
                        bold
                        isHighlight
                        numericValue={totals.final_cost_price}
                    />
                </Box>

                {/* Compact Profit Analysis */}
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: `1.5px solid ${theme.palette.primary.main}`,
                    }}
                >
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
                        <ProfitIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ fontSize: '0.85rem' }}>
                            {isArabic ? "تحليل الربح" : "Profit Analysis"}
                        </Typography>
                    </Stack>

                    <Stack spacing={1}>


                        <DetailRow
                            label={isArabic ? "الربح الإجمالي" : "Total Profit"}
                            value={formatCurrency(totals.final_profit)}
                            color="primary.main"
                            bold
                            numericValue={totals.final_profit}
                        />
                        <DetailRow
                            label={isArabic ? "الربح لكل فرد" : "Profit per Person"}
                            value={
                                totals.total_pax > 0
                                    ? formatCurrency(totals.final_profit / totals.total_pax)
                                    : formatCurrency(0)
                            }
                            color="primary.main"
                            numericValue={totals.total_pax > 0 ? totals.final_profit / totals.total_pax : 0}
                        />
                        <DetailRow
                            label={isArabic ? "هامش الربح" : "Profit Margin"}
                            value={
                                <Chip
                                    label={`${formatNumber(totals.profit_margin)}%`}
                                    color={
                                        totals.profit_margin >= 30
                                            ? "success"
                                            : totals.profit_margin >= 15
                                                ? "warning"
                                                : "error"
                                    }
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        minWidth: 50,
                                        height: 22,
                                        fontSize: '0.7rem',
                                    }}
                                />
                            }
                        />
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
};

export default QuotationSummary;
